import React from 'react'
import { Header } from './marketing/Header'
import Image from 'next/image'
import { format } from 'date-fns'
import { Blog, Label } from '../designsystem/Text'
import Meta from '../components/Meta'
import { FeatureAnnouncementBanner } from './marketing/FeatureAnnouncementBanner'
import HackerNewsLink from './HackerNewsLink'
import Section from './common/Section'
import { useRouter } from 'next/router'
import TwitterLogo from 'icons/TwitterLogo'
import HackerNewsLogo from 'icons/HackerNewsLogo'
import { Button } from 'designsystem/Button'
import cn from 'classnames'

interface BlogLayoutProps extends React.HTMLAttributes<HTMLElement> {
  meta: {
    title: string
    metaDescription: string
    metaImage?: string
    metaImageHeight?: number
    metaImageWidth?: number
    image: string
    authors: Array<string>
    avatars: Array<string>
    twitters: Array<string>
    date: Date
    path: string
    hero?: React.ReactNode
    bottomCtaLabel?: string
    bottomCtaLink?: string
  }
}

export function BlogLayout({ meta, ...props }: BlogLayoutProps): JSX.Element {
  const { asPath } = useRouter()

  const showBottomCta = meta.bottomCtaLabel && meta.bottomCtaLink

  return (
    <div className='bg-grey'>
      <Meta
        title={meta.title}
        description={meta.metaDescription}
        imageSrc={meta.metaImage}
        imageHeight={meta.metaImageHeight}
        imageWidth={meta.metaImageWidth}
      />
      <FeatureAnnouncementBanner />
      <Header />
      <main className='marketing-page'>
        <Section verticalPadding='first'>
          <div className='md:text-center md:flex md:flex-col md:items-center'>
            <Label className='text-neutral-60'>
              {meta.date.getFullYear() === new Date().getFullYear()
                ? format(meta.date, 'MMM d')
                : format(meta.date, 'MMM d, y')}
            </Label>

            <Blog.H1 className='spacer-3xs !text-29 sm:!text-38 md:!text-52 xl:!text-70'>
              {meta.title}
            </Blog.H1>

            <div className='max-w-xl spacer-xs'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <div className='whitespace-nowrap flex-shrink-0'>
                    {meta.avatars.map((avatar, i) => (
                      <div
                        key={`avatar-${avatar}`}
                        className={`inline-block ${i != 0 ? '-ml-3' : ''}`}
                      >
                        <a
                          href={`https://twitter.com/${meta.twitters[i]}`}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          <Image
                            alt={meta.twitters[i] + ' avatar'}
                            src={avatar}
                            width={32}
                            height={32}
                            className='box-content relative rounded-full border-3 border-grey'
                            priority
                          />
                        </a>
                      </div>
                    ))}
                  </div>

                  <div className='ml-3 sm:ml-4 font-demibold text-neutral-60'>
                    {meta.authors.map((author, i) => (
                      <p key={`author-${author}`} className='inline'>
                        {i > 0 && <span>{', '}</span>}
                        <a
                          href={`https://twitter.com/${meta.twitters[i]}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-block transition-colors hover:text-blue'
                        >
                          {author}
                        </a>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {meta.image && (
            <div className='relative spacer-lg w-full pt-16/9'>
              <Image
                alt="Blog post's hero image"
                className='rounded-12'
                src={meta.image}
                fill
                style={{ objectFit: 'cover', objectPosition: 'center' }}
              />
            </div>
          )}
        </Section>

        <Section noTopPadding>
          <div>{meta.hero}</div>

          <div className='rich-text'>
            <main className='spacer-sm' {...props} />

            {showBottomCta && (
              <div className='spacer-sm'>
                <a href={meta.bottomCtaLink} target='_blank' rel='noreferrer'>
                  <Button>{meta.bottomCtaLabel}</Button>
                </a>
              </div>
            )}

            <div
              className={cn(
                showBottomCta ? 'spacer-lg' : 'spacer-xl',
                'flex flex-col space-y-3 md-lg:flex-row md-lg:space-y-0 md-lg:space-x-10 md-lg:items-center',
              )}
            >
              <HackerNewsLink title={meta.title} path={asPath}>
                {({ href }) => (
                  <a
                    href={href}
                    target='_blank'
                    rel='noreferrer'
                    className='!no-underline w-fit-content'
                  >
                    <SocialButton
                      icon={
                        <HackerNewsLogo className='flex-shrink-0 h-7 w-auto' />
                      }
                      text='Discuss on HackerNews'
                    />
                  </a>
                )}
              </HackerNewsLink>
              <a
                target='_blank'
                rel='noreferrer'
                href={`https://mobile.twitter.com/intent/tweet?text=${encodeURIComponent(
                  `${meta.title} https://stellate.co${asPath} 👈`,
                )}`}
                className='!no-underline w-fit-content'
              >
                <SocialButton
                  icon={
                    <TwitterLogo className='fill-current flex-shrink-0 text-blue h-7 w-auto' />
                  }
                  text='Discuss on Twitter'
                />
              </a>
            </div>
          </div>
        </Section>
      </main>
    </div>
  )
}

interface SocialButtonProps extends React.HTMLAttributes<HTMLSpanElement> {
  text: string
  icon: JSX.Element
}

const SocialButton = ({ text, icon }: SocialButtonProps): JSX.Element => (
  <Button
    pageType='marketing'
    className='pr-5 flex space-x-2.5 items-center bg-neutral-10 hover:bg-neutral-15 text-neutral'
  >
    {icon}
    <span>{text}</span>
  </Button>
)
